import { Link, createFileRoute } from "@tanstack/react-router";
import {
  formatForDisplay,
  useHotkeys,
  type RegisterableHotkey,
} from "@tanstack/react-hotkeys";
import { usePostHog } from "@posthog/react";
import type {
  CandidateTimetable,
  CourseInput,
  GenerationResult,
  GeneratorConfig,
  RuleConfig,
} from "@better-ttb/generator";
import type {
  Course,
  DayNumber,
  DeliveryMode,
  Section,
  SectionCode,
  TeachMethod,
} from "@better-ttb/shared";
import { isSectionWaitlisted, millisofdayToHHMM } from "@better-ttb/shared";
import {
  Check,
  ChevronRight,
  Download,
  FileJson,
  Footprints,
  Hand,
  Layers,
  MousePointerClick,
  Redo2,
  RotateCcw,
  Trash2,
  TriangleAlert,
  Undo2,
  Upload,
  Wand2,
  X,
} from "lucide-react";
import * as React from "react";

import { AppHeader } from "@/components/app-header";
import { MobileNav } from "@/components/app-nav";
import { CourseDetailSheet } from "@/components/course/course-detail-sheet";
import { WeekGrid, walkConnectorTone } from "@/components/timetable/WeekGrid";
import type { BlockedWindow } from "@/components/timetable/WeekGrid";
import {
  GeneratePanelBody,
  candidateKey,
} from "@/components/timetable/GeneratePanelContent";
import { BUILDING_INDEX } from "@/lib/buildings";
import { daysWithClasses, hasTightTransfer } from "@/lib/itinerary";
import {
  buildAlternativeDraftBlocks,
  linkageImpact,
  walkFromPreviousBlock,
  type WalkFromPreviousBlock,
} from "@/lib/section-alternatives";
import { buildWalkSecondsMap } from "@/lib/walk-matrix";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import buildings from "@/data/buildings.json";
import {
  DAY_OPTIONS,
  DELIVERY_MODE_OPTIONS,
  RULE_DESCRIPTIONS,
  RULE_KIND_ORDER,
  RULE_LABELS,
  blockedWindowsFromRules,
  createDefaultRule,
  ensureBlockedTimesRule,
  minutesToMillis,
  removeRuleById,
  toggleBlockedCell,
  updateRuleById,
  type RuleKind,
} from "@/lib/generator-prefs";
import { buildIcsCalendar } from "@/lib/ics";
import { parsePlanJson } from "@/lib/plan-io";
import { useRequisiteGraph } from "@/lib/requisites/use-graph";
import {
  applyCandidateSelections,
  activeMeetingsForTerm,
  buildGeneratorCourseInputs,
  buildTermBlocks,
  computeCreditTotals,
  courseKey,
  daysOnCampusCount,
  detectLinkageViolationSectionKeys,
  findPlanSelectionIssues,
  getActivePlanCourses,
  pinnedKey,
  planSelectedFromTimetableSections,
  selectedSectionKey,
  selectedSectionsFromCandidate,
  selectedSectionsFromPlan,
  totalWalkMinutes,
  type PlanSelectedSection,
  type SelectedTimetableSection,
  type Term,
  type TimetableBlock,
} from "@/lib/timetable";
import { cn } from "@/lib/utils";
import { useCatalogForPlan } from "@/lib/use-catalog";
import { useCatalogStore } from "@/stores/catalog";
import {
  activePlanFromState,
  createDefaultGeneratorPrefs,
  selectCanRedo,
  selectCanUndo,
  type GeneratorPrefs,
  type GeneratorSortKey,
  type Plan,
  usePlanStore,
} from "@/stores/plan";
import type {
  GeneratorWorkerMessage,
  GeneratorWorkerRequest,
} from "@/workers/generator-contract";

export const Route = createFileRoute("/timetable")({
  head: () => ({ meta: [{ title: "Timetable · better-ttb" }] }),
  component: TimetableRoute,
});

interface BuildingRecord {
  code: string;
  lat: number;
  lng: number;
}

interface AltTarget {
  courseKey: string;
  teachMethod: TeachMethod;
  sectionName: string;
}

interface SlotPickerState {
  options: Section[];
  walkByName: Map<string, WalkFromPreviousBlock | null>;
}

const NO_ADD_RULE = "__add_rule__";
const TERMS: Term[] = ["fall", "winter"];
const TERM_LABELS: Record<Term, string> = {
  fall: "Fall",
  winter: "Winter",
};
const buildingCoordinates = Object.fromEntries(
  (buildings as BuildingRecord[]).map((building) => [
    building.code,
    { lat: building.lat, lng: building.lng },
  ]),
);

function TimetableRoute() {
  const posthog = usePostHog();
  const status = useCatalogStore((state) => state.status);
  const catalog = useCatalogStore((state) => state.catalog);
  const catalogError = useCatalogStore((state) => state.error);
  const refreshCatalogCourse = useCatalogStore((state) => state.refreshCourse);
  const plans = usePlanStore((state) => state.plans);
  const activePlanId = usePlanStore((state) => state.activePlanId);
  const choose = usePlanStore((state) => state.choose);
  const chooseMany = usePlanStore((state) => state.chooseMany);
  const clearChoice = usePlanStore((state) => state.clearChoice);
  const pinCourse = usePlanStore((state) => state.pin);
  const unpinCourse = usePlanStore((state) => state.unpin);
  const resetAllChoices = usePlanStore((state) => state.resetAllChoices);
  const undoSelections = usePlanStore((state) => state.undo);
  const redoSelections = usePlanStore((state) => state.redo);
  const hasUndoStep = usePlanStore(selectCanUndo);
  const hasRedoStep = usePlanStore(selectCanRedo);
  const importPlan = usePlanStore((state) => state.importPlan);
  const updatePlanPrefs = usePlanStore((state) => state.updatePlanPrefs);
  const activePlan = React.useMemo(
    () => activePlanFromState({ plans, activePlanId }),
    [activePlanId, plans],
  );
  const generatorPrefs = activePlan.prefs.generator ?? createDefaultGeneratorPrefs();
  const [term, setTerm] = React.useState<Term>("fall");
  const [panelOpen, setPanelOpen] = React.useState(true);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [blockoutMode, setBlockoutMode] = React.useState(false);
  const [selectedCourseKey, setSelectedCourseKey] = React.useState<string | null>(null);
  const [refreshingCourseKey, setRefreshingCourseKey] = React.useState<
    string | null
  >(null);
  const [refreshError, setRefreshError] = React.useState<{
    courseKey: string;
    message: string;
  } | null>(null);
  const [workerState, setWorkerState] = React.useState<"idle" | "running" | "done" | "error">("idle");
  const [workerError, setWorkerError] = React.useState<string | null>(null);
  const [generationResult, setGenerationResult] = React.useState<GenerationResult | null>(null);
  const [previewCandidate, setPreviewCandidate] = React.useState<CandidateTimetable | null>(null);
  const [altTarget, setAltTarget] = React.useState<AltTarget | null>(null);
  const [slotPicker, setSlotPicker] = React.useState<SlotPickerState | null>(null);
  const importInputRef = React.useRef<HTMLInputElement | null>(null);
  const workerRef = React.useRef<Worker | null>(null);
  useCatalogForPlan(activePlan);

  React.useEffect(
    () => () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    },
    [],
  );

  React.useEffect(() => {
    setAltTarget(null);
    setSlotPicker(null);
  }, [activePlanId, term]);

  React.useEffect(() => {
    if (previewCandidate) {
      setAltTarget(null);
      setSlotPicker(null);
    }
  }, [previewCandidate]);

  React.useEffect(() => {
    if (!altTarget) {
      return;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setAltTarget(null);
        setSlotPicker(null);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [altTarget]);

  // Mod maps to Cmd on macOS and Ctrl elsewhere. ignoreInputs keeps the plan
  // out of it while the caret is in a text field, where Mod+Z means "undo my
  // typing" (renaming a plan, editing a rule).
  useHotkeys(
    [
      { hotkey: "Mod+Z", callback: () => undoSelectionChange() },
      { hotkey: "Mod+Shift+Z", callback: () => redoSelectionChange() },
      { hotkey: "Mod+Y", callback: () => redoSelectionChange() },
    ],
    { ignoreInputs: true },
  );

  const coursesByKey = React.useMemo(() => {
    const map = new Map<string, Course>();

    catalog?.courses.forEach((course) => map.set(courseKey(course), course));
    return map;
  }, [catalog]);
  const graph = useRequisiteGraph(catalog?.courses);
  const planSelectionIssues = React.useMemo(
    () => findPlanSelectionIssues(activePlan, coursesByKey),
    [activePlan, coursesByKey],
  );
  const activeCourses = React.useMemo(
    () => getActivePlanCourses(activePlan, coursesByKey),
    [activePlan, coursesByKey],
  );
  const visibleSelections = React.useMemo(
    () =>
      previewCandidate
        ? selectedSectionsFromCandidate(activePlan, coursesByKey, previewCandidate)
        : selectedSectionsFromPlan(activePlan, coursesByKey),
    [activePlan, coursesByKey, previewCandidate],
  );
  const disallowedSectionKeys = React.useMemo(
    () =>
      detectLinkageViolationSectionKeys(
        planSelectedFromTimetableSections(visibleSelections),
      ),
    [visibleSelections],
  );
  const termData = React.useMemo(
    () => ({
      fall: buildTermBlocks(visibleSelections, "fall", {
        preview: Boolean(previewCandidate),
        disallowedSectionKeys,
      }),
      winter: buildTermBlocks(visibleSelections, "winter", {
        preview: Boolean(previewCandidate),
        disallowedSectionKeys,
      }),
    }),
    [disallowedSectionKeys, previewCandidate, visibleSelections],
  );
  const creditTotals = React.useMemo(
    () => computeCreditTotals(activePlan, coursesByKey),
    [activePlan, coursesByKey],
  );
  const hasConflicts = TERMS.some((entry) =>
    termData[entry].blocks.some((block) => block.conflict),
  );
  const hasDisallowed = TERMS.some((entry) =>
    termData[entry].blocks.some((block) => block.disallowed),
  );
  const tightTransferLink = React.useMemo(
    () => findTightTransferLink(visibleSelections),
    [visibleSelections],
  );
  const selectedCourse = selectedCourseKey ? coursesByKey.get(selectedCourseKey) ?? null : null;
  const planSelectedSections = React.useMemo(
    () =>
      planSelectedFromTimetableSections(
        selectedSectionsFromPlan(activePlan, coursesByKey),
      ),
    [activePlan, coursesByKey],
  );
  const altTargetCourse = altTarget
    ? coursesByKey.get(altTarget.courseKey) ?? null
    : null;
  const altTargetPinned = altTarget
    ? activePlan.pinned.find((entry) => pinnedKey(entry) === altTarget.courseKey) ?? null
    : null;
  const altDraftBlocks = React.useMemo(() => {
    if (!altTarget || !altTargetCourse || !altTargetPinned || previewCandidate) {
      return [];
    }

    return buildAlternativeDraftBlocks(
      altTargetCourse,
      altTarget.courseKey,
      altTarget.teachMethod,
      altTargetPinned.chosen,
      term,
      planSelectedSections,
    );
  }, [
    altTarget,
    altTargetCourse,
    altTargetPinned,
    planSelectedSections,
    previewCandidate,
    term,
  ]);
  const visibleGridBlocks = React.useMemo(
    () => [...termData[term].blocks, ...altDraftBlocks],
    [altDraftBlocks, term, termData],
  );
  const highlightedSectionKey =
    altTarget && altTargetCourse
      ? selectedSectionKey(
          { courseCode: altTargetCourse.code, sectionCode: altTargetCourse.sectionCode },
          altTarget.teachMethod,
          altTarget.sectionName,
        )
      : null;
  const selectedCoursePinned = selectedCourse
    ? activePlan.pinned.some(
        (entry) =>
          entry.courseCode === selectedCourse.code &&
          entry.sectionCode === selectedCourse.sectionCode,
      )
    : false;
  const blockedWindows = React.useMemo(
    () => blockedWindowsFromRules(generatorPrefs.rules),
    [generatorPrefs.rules],
  );
  const sortedCandidates = React.useMemo(
    () => sortCandidates(generationResult?.candidates ?? [], generatorPrefs.sort),
    [generationResult?.candidates, generatorPrefs.sort],
  );
  const previewKey = previewCandidate ? candidateKey(previewCandidate) : null;

  function setGeneratorPrefs(updater: (prefs: GeneratorPrefs) => GeneratorPrefs) {
    updatePlanPrefs(activePlan.id, (prefs) => ({
      ...prefs,
      generator: updater(prefs.generator ?? createDefaultGeneratorPrefs()),
    }));
  }

  async function refreshCourseData(course: Course) {
    const key = courseKey(course);

    setRefreshingCourseKey(key);
    setRefreshError(null);

    try {
      await refreshCatalogCourse(course);
    } catch (refreshErrorValue) {
      setRefreshError({
        courseKey: key,
        message:
          refreshErrorValue instanceof Error
            ? refreshErrorValue.message
            : String(refreshErrorValue),
      });
    } finally {
      setRefreshingCourseKey((current) => (current === key ? null : current));
    }
  }

  function setRules(rules: RuleConfig[]) {
    setGeneratorPrefs((prefs) => ({ ...prefs, rules }));
  }

  function setSort(sort: GeneratorSortKey) {
    setGeneratorPrefs((prefs) => ({ ...prefs, sort }));
  }

  function toggleBlockoutMode() {
    if (!blockoutMode) {
      setRules(ensureBlockedTimesRule(generatorPrefs.rules));
      // Enabling paint mode from inside the mobile drawer would hide the grid;
      // close the drawer so the user can immediately paint on the main grid.
      setDrawerOpen(false);
    }

    setBlockoutMode((current) => !current);
  }

  function paintBlockedCell(day: DayNumber, startMillis: number, endMillis: number) {
    setRules(toggleBlockedCell(generatorPrefs.rules, day, startMillis, endMillis));
  }

  function runGenerator() {
    const courses = buildGeneratorCourseInputs(activePlan, coursesByKey);

    if (courses.length === 0) {
      setWorkerState("error");
      setWorkerError("Pin at least one course before generating.");
      setGenerationResult(null);
      return;
    }

    workerRef.current?.terminate();
    // Only flatten walk-matrix pairs among the buildings that actually appear in
    // this plan's courses, keeping the worker message small.
    const walkSeconds = buildWalkSecondsMap(buildingCodesFromCourseInputs(courses));
    const request: GeneratorWorkerRequest = {
      type: "generate",
      id: createRequestId(),
      courses,
      config: {
        rules: generatorPrefs.rules,
        maxResults: 12,
        maxCombinations: 500_000,
        buildings: buildingCoordinates,
        walkSeconds,
      } satisfies GeneratorConfig,
    };
    const worker = new Worker(new URL("../workers/generator.worker.ts", import.meta.url), {
      type: "module",
    });

    workerRef.current = worker;
    setWorkerState("running");
    setWorkerError(null);
    setGenerationResult(null);
    setPreviewCandidate(null);

    posthog.capture("timetable_generated", {
      course_count: courses.length,
      rule_count: request.config.rules.length,
    });

    worker.addEventListener("message", (event: MessageEvent<GeneratorWorkerMessage>) => {
      if (event.data.id !== request.id) {
        return;
      }

      if (event.data.type === "started") {
        setWorkerState("running");
        return;
      }

      if (event.data.type === "done") {
        setGenerationResult(event.data.result);
        setWorkerState("done");
        worker.terminate();
        workerRef.current = null;
        return;
      }

      setWorkerError(event.data.message);
      setWorkerState("error");
      worker.terminate();
      workerRef.current = null;
    });

    worker.postMessage(request);
  }

  function applyPreview() {
    if (!previewCandidate) {
      return;
    }

    posthog.capture("generated_candidate_applied", {
      candidate_score: previewCandidate.score,
    });

    // One store update (one localStorage write) instead of one per selection,
    // so other tabs receive a single storage event rather than a burst.
    chooseMany(applyCandidateSelections(activePlan, coursesByKey, previewCandidate));
    setPreviewCandidate(null);
  }

  function commitSwitch(sectionName: string) {
    if (!altTarget || !altTargetCourse || !altTargetPinned) {
      return;
    }

    const candidate = altTargetCourse.sections.find(
      (section) =>
        section.teachMethod === altTarget.teachMethod &&
        section.name === sectionName,
    );

    if (!candidate) {
      return;
    }

    const impact = linkageImpact(
      altTargetCourse,
      altTarget.courseKey,
      altTargetPinned.chosen,
      altTarget.teachMethod,
      candidate,
      planSelectedSections,
    );
    const baseSelection = {
      courseCode: altTargetCourse.code,
      sectionCode: altTargetCourse.sectionCode,
    };

    chooseMany([
      {
        ...baseSelection,
        teachMethod: altTarget.teachMethod,
        sectionName,
      },
      ...impact.clears.map((teachMethod) => ({
        ...baseSelection,
        teachMethod,
        sectionName: null,
      })),
      ...impact.autoPicks.map((autoPick) => ({
        ...baseSelection,
        teachMethod: autoPick.teachMethod,
        sectionName: autoPick.sectionName,
      })),
    ]);

    posthog.capture("section_switched_via_grid", {
      course_code: altTargetCourse.code,
      teach_method: altTarget.teachMethod,
      cleared_count: impact.clears.length,
      autopicked_count: impact.autoPicks.length,
    });

    setAltTarget(null);
    setSlotPicker(null);
  }

  function openSlotPicker(block: TimetableBlock) {
    if (!altTarget || !altTargetCourse) {
      return;
    }

    const optionNames = new Set(block.draftOptions ?? []);
    const options = altTargetCourse.sections.filter(
      (section) =>
        section.teachMethod === altTarget.teachMethod &&
        optionNames.has(section.name),
    );

    if (options.length === 0) {
      return;
    }

    if (options.length === 1) {
      commitSwitch(options[0]!.name);
      return;
    }

    const enrolledBlocks = termData[term].blocks.filter(
      (entry) =>
        !(
          entry.courseKey === altTarget.courseKey &&
          entry.teachMethod === altTarget.teachMethod
        ),
    );
    const walkByName = new Map<string, WalkFromPreviousBlock | null>();

    for (const option of options) {
      walkByName.set(
        option.name,
        walkFromPreviousBlock(
          enrolledBlocks,
          block,
          buildingCodeForSlot(option, block, term),
        ),
      );
    }

    setSlotPicker({
      options: [...options].sort((left, right) =>
        compareWalkOptions(left, right, walkByName),
      ),
      walkByName,
    });
  }

  function handleBlockClick(block: TimetableBlock) {
    if (block.draft) {
      openSlotPicker(block);
      return;
    }

    if (altTarget) {
      setAltTarget(null);
      setSlotPicker(null);
      return;
    }

    setSelectedCourseKey(block.courseKey);
  }

  function handleBlockAltAction(block: TimetableBlock) {
    if (block.draft || block.preview) {
      return;
    }

    setSelectedCourseKey(null);
    setSlotPicker(null);
    setAltTarget({
      courseKey: block.courseKey,
      teachMethod: block.teachMethod,
      sectionName: block.sectionName,
    });
  }

  function undoSelectionChange() {
    // A preview is not in the plan yet, so there is nothing in the history to
    // step back to: undo simply drops it, which is what the grid shows.
    if (previewCandidate) {
      setPreviewCandidate(null);
      return;
    }

    if (!undoSelections()) {
      return;
    }

    closeSwitchUi();
    posthog.capture("timetable_selection_undone");
  }

  function redoSelectionChange() {
    // A preview covers the grid with sections that are not in the plan, so a
    // redo underneath it would be invisible and then be overwritten by Apply.
    // Undo takes the preview back; redo waits until it is resolved.
    if (previewCandidate || !redoSelections()) {
      return;
    }

    closeSwitchUi();
    posthog.capture("timetable_selection_redone");
  }

  // The switch flow points at a section that undo/redo may have just replaced,
  // so both close it rather than leaving stale draft blocks on the grid.
  function closeSwitchUi() {
    setAltTarget(null);
    setSlotPicker(null);
  }

  function exportPlanJson() {
    posthog.capture("plan_exported_json", {
      pinned_course_count: activePlan.pinned.length,
    });
    downloadText(
      `${activePlan.name.replace(/\W+/g, "-").toLowerCase()}-plan.json`,
      "application/json",
      JSON.stringify(activePlan, null, 2),
    );
  }

  async function importPlanJson(file: File) {
    const plan = parsePlanJson(await file.text());

    if (!plan) {
      window.alert("The selected file is not a valid better-ttb plan export.");
      return;
    }

    importPlan(plan, `${plan.name} Import`);
    posthog.capture("plan_imported", {
      pinned_course_count: plan.pinned.length,
    });
  }

  function exportIcs() {
    posthog.capture("plan_exported_ics", {
      pinned_course_count: activePlan.pinned.length,
    });
    const selectedSections = selectedSectionsFromPlan(activePlan, coursesByKey);
    const ics = buildIcsCalendar({
      calendarName: activePlan.name,
      selectedSections,
    });

    downloadText(
      `${activePlan.name.replace(/\W+/g, "-").toLowerCase()}-timetable.ics`,
      "text/calendar;charset=utf-8",
      ics,
    );
  }

  return (
    <TooltipProvider>
      <main className="flex h-dvh flex-col overflow-hidden bg-background text-foreground">
        <AppHeader
          brandIcon={Layers}
          onActivePlanChange={() => setPreviewCandidate(null)}
          actions={
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="icon-sm">
                    <FileJson />
                    <span className="sr-only">Plan import and export</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-2">
                  <div className="space-y-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start"
                      onClick={exportPlanJson}
                    >
                      <Download />
                      Export JSON
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => importInputRef.current?.click()}
                    >
                      <Upload />
                      Import JSON
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start sm:hidden"
                      onClick={exportIcs}
                    >
                      <Download />
                      Export ICS
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="hidden sm:inline-flex"
                onClick={exportIcs}
              >
                <Download />
                ICS
              </Button>
            </>
          }
        />

        <input
          ref={importInputRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = "";

            if (file) {
              void importPlanJson(file);
            }
          }}
        />

        <div
          className="grid min-h-0 flex-1 grid-cols-1 border-t pb-16 md:pb-0 lg:grid-cols-[var(--tt-cols)]"
          style={
            {
              "--tt-cols": panelOpen
                ? "minmax(0,1fr) minmax(340px,390px)"
                : "minmax(0,1fr) 44px",
            } as React.CSSProperties
          }
        >
          <section className="min-h-0 overflow-y-auto bg-muted/20 p-4">
            <div className="mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold">Timetable</h2>
                  <p className="text-sm text-muted-foreground">
                    {activeCourses.length} pinned courses · {visibleSelections.length} scheduled sections
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {tightTransferLink && (
                    <Button asChild type="button" size="sm" variant="outline" className="border-destructive/50 text-destructive hover:text-destructive">
                      <Link to="/map" search={tightTransferLink}>
                        <TriangleAlert />
                        Tight walk
                      </Link>
                    </Button>
                  )}
                  <UndoRedoButtons
                    previewing={Boolean(previewCandidate)}
                    canUndo={hasUndoStep}
                    canRedo={hasRedoStep}
                    onUndo={undoSelectionChange}
                    onRedo={redoSelectionChange}
                  />
                  <Tabs value={term} onValueChange={(value) => setTerm(value as Term)}>
                    <TabsList>
                      <TabsTrigger value="fall">
                        Fall
                        <Badge variant="secondary">{creditTotals.fall.toFixed(1)}</Badge>
                      </TabsTrigger>
                      <TabsTrigger value="winter">
                        Winter
                        <Badge variant="secondary">{creditTotals.winter.toFixed(1)}</Badge>
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>

              {catalogError && <Banner tone="warn">Catalog warning: {catalogError}</Banner>}
              {status === "loading" && <Banner>Loading catalog for this plan.</Banner>}
              {status === "empty" && (
                <Banner tone="warn">
                  Catalog not scraped yet. Run POST /api/admin/scrape to populate courses.
                </Banner>
              )}
              {hasConflicts && (
                <Banner tone="error">
                  Conflicts detected. Overlapping blocks are outlined in red.
                </Banner>
              )}
              {hasDisallowed && (
                <Banner tone="warn">
                  Invalid selections detected. Some chosen sections must be taken together with a different lecture or tutorial — greyed blocks show which.
                </Banner>
              )}
              {planSelectionIssues.length > 0 && (
                <Banner tone="warn">
                  {planSelectionIssues.length} saved section selection
                  {planSelectionIssues.length === 1 ? " is" : "s are"} no longer available.
                  Choose a replacement before generating or exporting this plan.
                </Banner>
              )}
              {previewCandidate && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed bg-background p-3">
                  <div>
                    <p className="text-sm font-medium">Preview mode</p>
                    <p className="text-xs text-muted-foreground">
                      Dashed blocks show the generated candidate before it is written to the plan.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm" onClick={applyPreview}>
                      <Check />
                      Apply
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewCandidate(null)}
                    >
                      <X />
                      Discard
                    </Button>
                  </div>
                </div>
              )}
              {altTarget && altTargetCourse && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed bg-background p-3">
                  <div>
                    <p className="text-sm font-medium">
                      Switching {altTargetCourse.code} {altTarget.teachMethod}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {altDraftBlocks.length > 0
                        ? "Click a dashed block, Esc or X to cancel."
                        : "No other sections are available for this term. Esc or X to cancel."}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setAltTarget(null);
                      setSlotPicker(null);
                    }}
                  >
                    <X />
                    Cancel
                  </Button>
                </div>
              )}

              <WeekGrid
                blocks={visibleGridBlocks}
                blockedWindows={blockedWindows}
                blockoutEnabled={blockoutMode}
                highlightSectionKey={highlightedSectionKey}
                onBlockAltAction={handleBlockAltAction}
                onPaintCell={paintBlockedCell}
                onBlockClick={handleBlockClick}
              />
              <GridActionLegend />

              <UnscheduledList
                term={term}
                creditTotal={term === "fall" ? creditTotals.fall : creditTotals.winter}
                unscheduled={termData[term].unscheduled}
              />
            </div>
          </section>

          {panelOpen ? (
            <GeneratePanel
              className="hidden md:flex"
              activePlan={activePlan}
              courses={activeCourses}
              rules={generatorPrefs.rules}
              blockedWindows={blockedWindows}
              blockoutMode={blockoutMode}
              workerState={workerState}
              workerError={workerError}
              result={generationResult}
              sort={generatorPrefs.sort}
              sortedCandidates={sortedCandidates}
              previewKey={previewKey}
              onClose={() => setPanelOpen(false)}
              onResetAll={resetAllChoices}
              onRun={runGenerator}
              onRulesChange={setRules}
              onToggleBlockout={toggleBlockoutMode}
              onPreviewCandidate={setPreviewCandidate}
              onApplyPreview={applyPreview}
              onDiscardPreview={() => setPreviewCandidate(null)}
              onSortChange={setSort}
            />
          ) : (
            <aside className="hidden items-start justify-center border-l bg-background p-2 md:flex">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setPanelOpen(true)}
                  >
                    <Wand2 />
                    <span className="sr-only">Open generator</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open generator</TooltipContent>
              </Tooltip>
            </aside>
          )}
        </div>

        <Button
          type="button"
          className="fixed right-4 bottom-20 z-40 shadow-lg md:hidden"
          onClick={() => setDrawerOpen(true)}
        >
          <Wand2 />
          Generate
        </Button>

        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerContent className="h-[85vh] md:hidden">
            <DrawerHeader className="border-b text-left">
              <DrawerTitle>Generate</DrawerTitle>
              <DrawerDescription>Rules, locks, and candidates</DrawerDescription>
            </DrawerHeader>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <GeneratePanelBody
                activePlan={activePlan}
                courses={activeCourses}
                rules={generatorPrefs.rules}
                blockedWindows={blockedWindows}
                blockoutMode={blockoutMode}
                workerState={workerState}
                workerError={workerError}
                result={generationResult}
                sort={generatorPrefs.sort}
                sortedCandidates={sortedCandidates}
                previewKey={previewKey}
                renderLockedChoices={() => (
                  <LockedChoicesHint
                    activePlan={activePlan}
                    courses={activeCourses}
                    onResetAll={resetAllChoices}
                  />
                )}
                renderRuleEditor={() => (
                  <RuleEditor rules={generatorPrefs.rules} onRulesChange={setRules} />
                )}
                onRun={runGenerator}
                onToggleBlockout={toggleBlockoutMode}
                onPreviewCandidate={setPreviewCandidate}
                onApplyPreview={applyPreview}
                onDiscardPreview={() => setPreviewCandidate(null)}
                onSortChange={setSort}
              />
            </div>
          </DrawerContent>
        </Drawer>

        <CourseDetailSheet
          course={selectedCourse}
          activePlan={activePlan}
          planSelectedSections={planSelectedSections}
          refreshError={
            selectedCourseKey === refreshError?.courseKey
              ? refreshError.message
              : null
          }
          refreshing={selectedCourseKey === refreshingCourseKey}
          pinned={selectedCoursePinned}
          graph={graph}
          onChoose={choose}
          onClearChoice={clearChoice}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedCourseKey(null);
            }
          }}
          onPin={(course) => pinCourse(course.code, course.sectionCode)}
          onUnpin={(course) => unpinCourse(course.code, course.sectionCode)}
          onRefresh={refreshCourseData}
          onOpenCourse={(code) => {
            const resolved = resolveCourseKey(code, coursesByKey);
            if (resolved) {
              setSelectedCourseKey(resolved);
            }
          }}
        />

        <SlotPickerDialog
          slotPicker={slotPicker}
          course={altTargetCourse}
          chosen={altTargetPinned?.chosen ?? {}}
          target={altTarget}
          planSelectedSections={planSelectedSections}
          onChoose={commitSwitch}
          onOpenChange={(open) => {
            if (!open) {
              setSlotPicker(null);
            }
          }}
        />

        <MobileNav />
      </main>
    </TooltipProvider>
  );
}

function GeneratePanel({
  className,
  activePlan,
  courses,
  rules,
  blockedWindows,
  blockoutMode,
  workerState,
  workerError,
  result,
  sort,
  sortedCandidates,
  previewKey,
  onClose,
  onResetAll,
  onRun,
  onRulesChange,
  onToggleBlockout,
  onPreviewCandidate,
  onApplyPreview,
  onDiscardPreview,
  onSortChange,
}: {
  className?: string;
  activePlan: Plan;
  courses: Course[];
  rules: RuleConfig[];
  blockedWindows: BlockedWindow[];
  blockoutMode: boolean;
  workerState: "idle" | "running" | "done" | "error";
  workerError: string | null;
  result: GenerationResult | null;
  sort: GeneratorSortKey;
  sortedCandidates: CandidateTimetable[];
  previewKey: string | null;
  onClose: () => void;
  onResetAll: () => void;
  onRun: () => void;
  onRulesChange: (rules: RuleConfig[]) => void;
  onToggleBlockout: () => void;
  onPreviewCandidate: (candidate: CandidateTimetable) => void;
  onApplyPreview: () => void;
  onDiscardPreview: () => void;
  onSortChange: (sort: GeneratorSortKey) => void;
}) {
  return (
    <aside className={cn("min-h-0 flex-col overflow-y-auto border-l bg-background", className)}>
      <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background p-4">
        <div>
          <h2 className="text-sm font-semibold">Generate</h2>
          <p className="text-xs text-muted-foreground">Rules, locks, and candidates</p>
        </div>
        <Button type="button" variant="ghost" size="icon-sm" onClick={onClose}>
          <ChevronRight />
          <span className="sr-only">Collapse generator</span>
        </Button>
      </div>

      <GeneratePanelBody
        activePlan={activePlan}
        courses={courses}
        rules={rules}
        blockedWindows={blockedWindows}
        blockoutMode={blockoutMode}
        workerState={workerState}
        workerError={workerError}
        result={result}
        sort={sort}
        sortedCandidates={sortedCandidates}
        previewKey={previewKey}
        renderLockedChoices={() => (
          <LockedChoicesHint
            activePlan={activePlan}
            courses={courses}
            onResetAll={onResetAll}
          />
        )}
        renderRuleEditor={() => (
          <RuleEditor rules={rules} onRulesChange={onRulesChange} />
        )}
        onRun={onRun}
        onToggleBlockout={onToggleBlockout}
        onPreviewCandidate={onPreviewCandidate}
        onApplyPreview={onApplyPreview}
        onDiscardPreview={onDiscardPreview}
        onSortChange={onSortChange}
      />
    </aside>
  );
}

// Chosen sections are auto-locked by the generator now, so this is a read-only
// summary of what will stay fixed rather than an interactive lock toggle.
function LockedChoicesHint({
  activePlan,
  courses,
  onResetAll,
}: {
  activePlan: Plan;
  courses: Course[];
  onResetAll: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const lockedCourses = courses.flatMap((course) => {
    const key = courseKey(course);
    const pinned = activePlan.pinned.find((entry) => pinnedKey(entry) === key);

    if (!pinned) {
      return [];
    }

    const choices = Object.entries(pinned.chosen)
      .filter((entry): entry is [string, string] =>
        typeof entry[1] === "string" && entry[1].length > 0,
      )
      .map(([teachMethod, sectionName]) => `${teachMethod} ${sectionName}`);

    if (choices.length === 0) {
      return [];
    }

    return [{ code: course.code, key, choices }];
  });

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Locked choices</h3>
        {lockedCourses.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => setConfirmOpen(true)}
          >
            <RotateCcw />
            Reset all to Auto
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Sections you&apos;ve picked in your plan are kept fixed. Clear a section
        choice (set it to Auto) to let the generator optimize it.
      </p>
      {lockedCourses.length > 0 && (
        <ul className="space-y-1.5">
          {lockedCourses.map((entry) => (
            <li key={entry.key} className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="font-medium">{entry.code}</span>
              {entry.choices.map((choice) => (
                <Badge key={choice} variant="secondary" className="font-normal">
                  {choice}
                </Badge>
              ))}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset all courses to Auto?</DialogTitle>
            <DialogDescription>
              This clears every locked section choice across all{" "}
              {lockedCourses.length} course{lockedCourses.length === 1 ? "" : "s"}{" "}
              in this plan, letting the generator optimize each one. Your pinned
              courses stay pinned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                onResetAll();
                setConfirmOpen(false);
              }}
            >
              <RotateCcw />
              Reset all to Auto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function RuleEditor({
  rules,
  onRulesChange,
}: {
  rules: RuleConfig[];
  onRulesChange: (rules: RuleConfig[]) => void;
}) {
  const remainingKinds = RULE_KIND_ORDER.filter(
    (kind) => !rules.some((rule) => rule.kind === kind),
  );

  function update(ruleId: string, updater: (rule: RuleConfig) => RuleConfig) {
    onRulesChange(updateRuleById(rules, ruleId, updater));
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {rules.map((rule) => (
          <div key={rule.id} className="space-y-3 rounded-md border p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">{RULE_LABELS[rule.kind]}</p>
                <p className="text-xs text-muted-foreground">{RULE_DESCRIPTIONS[rule.kind]}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Soft</span>
                <Switch
                  checked={rule.mode === "hard"}
                  onCheckedChange={(checked) =>
                    update(rule.id, (current) => ({
                      ...current,
                      mode: checked ? "hard" : "soft",
                    }) as RuleConfig)
                  }
                />
                <span className="text-xs text-muted-foreground">Hard</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => onRulesChange(removeRuleById(rules, rule.id))}
                >
                  <Trash2 />
                  <span className="sr-only">Remove rule</span>
                </Button>
              </div>
            </div>

            <RuleParamControls
              rule={rule}
              onChange={(nextRule) => update(rule.id, () => nextRule)}
            />

            {rule.mode === "soft" && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Weight</span>
                  <span>{rule.weight.toFixed(2)}</span>
                </div>
                <Slider
                  value={[rule.weight]}
                  min={0}
                  max={1}
                  step={0.05}
                  onValueChange={(value) =>
                    update(rule.id, (current) => ({
                      ...current,
                      weight: value[0] ?? current.weight,
                    }) as RuleConfig)
                  }
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <Select
        value={NO_ADD_RULE}
        onValueChange={(value) => {
          if (value !== NO_ADD_RULE) {
            onRulesChange([...rules, createDefaultRule(value as RuleKind)]);
          }
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Add rule" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_ADD_RULE} disabled>
            Add rule
          </SelectItem>
          {remainingKinds.map((kind) => (
            <SelectItem key={kind} value={kind}>
              {RULE_LABELS[kind]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function RuleParamControls({
  rule,
  onChange,
}: {
  rule: RuleConfig;
  onChange: (rule: RuleConfig) => void;
}) {
  switch (rule.kind) {
    case "max-gap":
      return (
        <NumberSlider
          label="Max gap"
          value={rule.maxGapMinutes}
          min={0}
          max={240}
          step={15}
          suffix="min"
          onChange={(value) => onChange({ ...rule, maxGapMinutes: value })}
        />
      );
    case "max-walk":
      return (
        <NumberSlider
          label="Max walk"
          value={rule.maxWalkMinutes}
          min={0}
          max={30}
          step={1}
          suffix="min"
          onChange={(value) => onChange({ ...rule, maxWalkMinutes: value })}
        />
      );
    case "blocked-times":
      return (
        <p className="text-xs text-muted-foreground">
          {rule.windows.length} windows. Use Block out to paint cells on the grid.
        </p>
      );
    case "earliest-start":
      return (
        <TimeSelect
          label="No starts before"
          value={rule.millisofday}
          onChange={(value) => onChange({ ...rule, millisofday: value })}
        />
      );
    case "latest-end":
      return (
        <TimeSelect
          label="No ends after"
          value={rule.millisofday}
          onChange={(value) => onChange({ ...rule, millisofday: value })}
        />
      );
    case "days-off":
      return (
        <div className="space-y-3">
          <NumberSlider
            label="Free days target"
            value={rule.count ?? 1}
            min={0}
            max={4}
            step={1}
            suffix="days"
            onChange={(value) => {
              onChange(makeDaysOffRule(rule, [], value));
            }}
          />
          <div className="flex flex-wrap gap-1">
            {DAY_OPTIONS.map((day) => {
              const active = rule.days?.includes(day.value) ?? false;

              return (
                <Button
                  key={day.value}
                  type="button"
                  variant={active ? "secondary" : "outline"}
                  size="xs"
                  onClick={() => {
                    const days = toggleValue(rule.days ?? [], day.value);
                    onChange(makeDaysOffRule(rule, days, rule.count ?? 1));
                  }}
                >
                  {day.label}
                </Button>
              );
            })}
          </div>
        </div>
      );
    case "compactness":
      return (
        <LabeledSelect
          label="Preference"
          value={rule.preference}
          options={[
            { value: "compact", label: "Compact" },
            { value: "spread", label: "Spread" },
          ]}
          onChange={(value) =>
            onChange({ ...rule, preference: value as "compact" | "spread" })
          }
        />
      );
    case "lunch-break":
      return (
        <div className="grid gap-2">
          <TimeSelect
            label="Lunch starts"
            value={rule.startMillis}
            onChange={(value) => onChange({ ...rule, startMillis: value })}
          />
          <TimeSelect
            label="Lunch ends"
            value={rule.endMillis}
            onChange={(value) => onChange({ ...rule, endMillis: value })}
          />
          <NumberSlider
            label="Minimum break"
            value={rule.minMinutes}
            min={15}
            max={90}
            step={15}
            suffix="min"
            onChange={(value) => onChange({ ...rule, minMinutes: value })}
          />
        </div>
      );
    case "avoid-full-sections":
    case "avoid-waitlist":
      return <p className="text-xs text-muted-foreground">No parameters.</p>;
    case "prefer-delivery":
      return (
        <div className="flex flex-wrap gap-1">
          {DELIVERY_MODE_OPTIONS.map((mode) => {
            const active = rule.modes.includes(mode.value);

            return (
              <Button
                key={mode.value}
                type="button"
                variant={active ? "secondary" : "outline"}
                size="xs"
                onClick={() =>
                  onChange({
                    ...rule,
                    modes: toggleValue(rule.modes, mode.value) as DeliveryMode[],
                  })
                }
              >
                {mode.label}
              </Button>
            );
          })}
        </div>
      );
    case "prefer-instructor":
      return (
        <Input
          value={rule.names.join(", ")}
          placeholder="Instructor names"
          onChange={(event) =>
            onChange({
              ...rule,
              names: event.target.value
                .split(",")
                .map((name) => name.trim())
                .filter(Boolean),
            })
          }
        />
      );
  }
}

function NumberSlider({
  label,
  value,
  min,
  max,
  step,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span>
          {value} {suffix}
        </span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(next) => onChange(next[0] ?? value)}
      />
    </div>
  );
}

function TimeSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  const options = React.useMemo(() => buildTimeOptions(7, 22), []);

  return (
    <LabeledSelect
      label={label}
      value={String(value)}
      options={options.map((option) => ({
        value: String(option.value),
        label: option.label,
      }))}
      onChange={(next) => onChange(Number(next))}
    />
  );
}

function LabeledSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger size="sm" className="w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function UnscheduledList({
  term,
  creditTotal,
  unscheduled,
}: {
  term: Term;
  creditTotal: number;
  unscheduled: ReturnType<typeof buildTermBlocks>["unscheduled"];
}) {
  return (
    <section className="rounded-md border bg-background p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{TERM_LABELS[term]} details</h3>
        <Badge variant="outline">{creditTotal.toFixed(1)} credits</Badge>
      </div>
      {unscheduled.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {unscheduled.map((section) => (
            <Badge key={section.key} variant="secondary">
              {section.courseCode} {section.sectionName}: {section.reason}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          No chosen sections without meeting times.
        </p>
      )}
    </section>
  );
}

const SECTION_PREFERENCE: SectionCode[] = ["F", "S", "Y"];

/**
 * Resolves a course reference (bare code like "CSC207H1" or a full
 * "code:sectionCode" key) to a key present in `coursesByKey`, preferring
 * F/S/Y offerings in that order when only a bare code is given.
 */
function resolveCourseKey(
  value: string,
  coursesByKey: Map<string, Course>,
): string | null {
  const [code, section] = value.split(":");

  if (section) {
    return coursesByKey.has(value) ? value : null;
  }

  for (const preference of SECTION_PREFERENCE) {
    const candidate = `${code}:${preference}`;

    if (coursesByKey.has(candidate)) {
      return candidate;
    }
  }

  return null;
}

function Banner({
  children,
  tone = "info",
}: {
  children: React.ReactNode;
  tone?: "info" | "warn" | "error";
}) {
  return (
    <div
      className={cn(
        "rounded-md border bg-background px-3 py-2 text-sm",
        tone === "warn" &&
          "border-amber-400/50 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200",
        tone === "error" && "border-destructive/40 bg-destructive/5 text-destructive",
      )}
    >
      {children}
    </div>
  );
}

function SlotPickerDialog({
  slotPicker,
  course,
  chosen,
  target,
  planSelectedSections,
  onChoose,
  onOpenChange,
}: {
  slotPicker: SlotPickerState | null;
  course: Course | null;
  chosen: Record<string, string | null>;
  target: AltTarget | null;
  planSelectedSections: readonly PlanSelectedSection[];
  onChoose: (sectionName: string) => void;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={slotPicker !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Which section?</DialogTitle>
          <DialogDescription>
            Multiple sections share this time slot. Pick one to switch the whole section.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {slotPicker?.options.map((section) => {
            const walk = slotPicker.walkByName.get(section.name) ?? null;
            const impactNote =
              course && target
                ? describeLinkageImpact(
                    course,
                    target.courseKey,
                    chosen,
                    target.teachMethod,
                    section,
                    planSelectedSections,
                  )
                : null;

            return (
              <button
                key={section.name}
                type="button"
                className="w-full rounded-md border bg-background p-3 text-left transition-colors hover:bg-muted/50 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                onClick={() => onChoose(section.name)}
              >
                <span className="flex flex-wrap items-start justify-between gap-3">
                  <span className="min-w-0 space-y-1">
                    <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                      {section.name}
                      {isSectionWaitlisted(section) && (
                        <Badge variant="outline" className="px-1 py-0 text-[10px] font-normal text-amber-700 dark:text-amber-400">
                          WL
                        </Badge>
                      )}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {formatInstructors(section)}
                    </span>
                    {impactNote && (
                      <span className="block text-xs text-destructive">
                        {impactNote}
                      </span>
                    )}
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-1 text-xs">
                    <span className="text-muted-foreground">
                      {section.currentEnrolment}/{section.maxEnrolment}
                    </span>
                    {walk && (
                      <span
                        className={cn(
                          "flex items-center gap-1 rounded-full border border-white/20 bg-neutral-900/95 px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums shadow-sm dark:border-white/15 dark:bg-neutral-950/95",
                          walkConnectorTone(walk.minutes),
                        )}
                      >
                        <Footprints className="size-3 shrink-0" aria-hidden="true" />
                        <span>
                          ~{walk.minutes} min from {walk.fromCode}
                        </span>
                      </span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Undo/redo controls for the grid. They carry the feature on touch devices,
 * where the hotkeys are unreachable, and show the shortcut everywhere else.
 */
function UndoRedoButtons({
  previewing,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}: {
  previewing: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}) {
  const undoShortcut = useHotkeyLabel("Mod+Z");
  const redoShortcut = useHotkeyLabel("Mod+Shift+Z");
  // While a preview is up, undo takes the preview back rather than stepping
  // the plan's history, and redo stays out of the way until it is resolved.
  const undoLabel = previewing ? "Discard preview" : "Undo section change";

  return (
    <div className="flex items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={!previewing && !canUndo}
            onClick={onUndo}
          >
            <Undo2 />
            <span className="sr-only">{undoLabel}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{withShortcut(undoLabel, undoShortcut)}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={previewing || !canRedo}
            onClick={onRedo}
          >
            <Redo2 />
            <span className="sr-only">Redo section change</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {withShortcut("Redo section change", redoShortcut)}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function withShortcut(label: string, shortcut: string | null): string {
  return shortcut ? `${label} · ${shortcut}` : label;
}

function GridActionLegend() {
  const coarse = useCoarsePointer();
  const undoHint = useUndoHint();

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <MousePointerClick className="size-3.5" aria-hidden="true" />
        {coarse ? "Tap — course details" : "Click — course details"}
      </span>
      <span aria-hidden="true">·</span>
      <span className="inline-flex items-center gap-1">
        <Hand className="size-3.5" aria-hidden="true" />
        {coarse ? "Press & hold — switch section" : "Right-click — switch section"}
      </span>
      {undoHint && (
        <>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1">
            <Undo2 className="size-3.5" aria-hidden="true" />
            {undoHint}
          </span>
        </>
      )}
    </div>
  );
}

/**
 * The grid's undo/redo hint. Coarse pointers have no modifier keys to press
 * and use the toolbar buttons instead.
 */
function useUndoHint(): string | null {
  const coarse = useCoarsePointer();
  const undoShortcut = useHotkeyLabel("Mod+Z");
  const redoShortcut = useHotkeyLabel("Mod+Shift+Z");

  if (coarse || !undoShortcut || !redoShortcut) {
    return null;
  }

  return `${undoShortcut} undo · ${redoShortcut} redo`;
}

/**
 * A hotkey's platform-specific label, resolved after mount: formatForDisplay
 * reads the platform, so rendering it during SSR would mismatch on hydration.
 */
function useHotkeyLabel(hotkey: RegisterableHotkey): string | null {
  const [label, setLabel] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLabel(formatForDisplay(hotkey));
  }, [hotkey]);

  return label;
}

function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const query = window.matchMedia("(pointer: coarse)");
    const update = () => setCoarse(query.matches);

    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return coarse;
}

function describeLinkageImpact(
  course: Course,
  courseKey: string,
  chosen: Record<string, string | null>,
  teachMethod: TeachMethod,
  candidate: Section,
  planSelectedSections: readonly PlanSelectedSection[],
): string | null {
  const impact = linkageImpact(
    course,
    courseKey,
    chosen,
    teachMethod,
    candidate,
    planSelectedSections,
  );

  if (impact.clears.length === 0) {
    return null;
  }

  return impact.clears
    .map((clearedTeachMethod) => {
      const autoPick = impact.autoPicks.find(
        (entry) => entry.teachMethod === clearedTeachMethod,
      );

      if (autoPick) {
        return `switches ${clearedTeachMethod} to ${autoPick.sectionName}`;
      }

      return `clears ${chosen[clearedTeachMethod] ?? clearedTeachMethod}`;
    })
    .join(" · ");
}

function formatInstructors(section: Section): string {
  const names = section.instructors
    .map((instructor) => `${instructor.firstName} ${instructor.lastName}`.trim())
    .filter(Boolean);

  return names.length > 0 ? names.join(", ") : "Instructor TBA";
}

function buildingCodeForSlot(
  section: Section,
  block: TimetableBlock,
  term: Term,
): string {
  return (
    activeMeetingsForTerm(section, term).find(
      (meeting) =>
        meeting.start.day === block.day &&
        meeting.start.millisofday === block.startMillis &&
        meeting.end.millisofday === block.endMillis,
    )?.building.buildingCode.trim() ?? ""
  );
}

function compareWalkOptions(
  left: Section,
  right: Section,
  walkByName: Map<string, WalkFromPreviousBlock | null>,
): number {
  const leftWalk = walkByName.get(left.name) ?? null;
  const rightWalk = walkByName.get(right.name) ?? null;

  if (leftWalk && rightWalk) {
    return leftWalk.minutes - rightWalk.minutes || left.name.localeCompare(right.name);
  }

  if (leftWalk) {
    return -1;
  }

  if (rightWalk) {
    return 1;
  }

  return left.name.localeCompare(right.name);
}

function sortCandidates(
  candidates: readonly CandidateTimetable[],
  sort: GeneratorSortKey,
): CandidateTimetable[] {
  return [...candidates].sort((left, right) => {
    switch (sort) {
      case "walking":
        return totalWalkMinutes(left) - totalWalkMinutes(right) || right.score - left.score;
      case "earliest-start":
        return (
          (left.extras.earliestStart ?? Number.MAX_SAFE_INTEGER) -
            (right.extras.earliestStart ?? Number.MAX_SAFE_INTEGER) ||
          right.score - left.score
        );
      case "days-on-campus":
        return daysOnCampusCount(left) - daysOnCampusCount(right) || right.score - left.score;
      case "score":
        return right.score - left.score;
    }
  });
}

function findTightTransferLink(
  selectedSections: SelectedTimetableSection[],
): { term: Term; day: DayNumber } | null {
  for (const term of TERMS) {
    if (!hasTightTransfer(selectedSections, BUILDING_INDEX, term)) {
      continue;
    }

    const days = daysWithClasses(selectedSections, BUILDING_INDEX, term);

    return { term, day: days[0] ?? 1 };
  }

  return null;
}

function buildTimeOptions(startHour: number, endHour: number): Array<{ value: number; label: string }> {
  const options: Array<{ value: number; label: string }> = [];

  for (let minutes = startHour * 60; minutes <= endHour * 60; minutes += 30) {
    const value = minutesToMillis(minutes);
    options.push({ value, label: millisofdayToHHMM(value) });
  }

  return options;
}

function toggleValue<T>(values: readonly T[], value: T): T[] {
  return values.includes(value)
    ? values.filter((entry) => entry !== value)
    : [...values, value];
}

function makeDaysOffRule(
  rule: Extract<RuleConfig, { kind: "days-off" }>,
  days: DayNumber[],
  count: number,
): Extract<RuleConfig, { kind: "days-off" }> {
  const next: Extract<RuleConfig, { kind: "days-off" }> = {
    id: rule.id,
    kind: rule.kind,
    mode: rule.mode,
    weight: rule.weight,
  };

  if (days.length > 0) {
    next.days = days;
  } else {
    next.count = count;
  }

  return next;
}

function downloadText(filename: string, type: string, content: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function buildingCodesFromCourseInputs(courses: readonly CourseInput[]): string[] {
  const codes = new Set<string>();

  for (const input of courses) {
    for (const section of input.course.sections) {
      for (const meeting of section.meetingTimes) {
        const code = meeting.building.buildingCode.trim();

        if (code) {
          codes.add(code);
        }
      }
    }
  }

  return [...codes];
}

function createRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return Math.random().toString(36).slice(2, 10);
}
